import { Component } from 'react';
import { AlertTriangle, Home, RefreshCw, Send, CheckCircle } from 'lucide-react';
import api from '../utils/api';
import { isStaleChunkError, reloadForStaleChunk } from '../utils/staleChunk';

/**
 * ErrorBoundary global — captura errores de render en cualquier página/componente
 * y muestra un fallback con CTA "volver al inicio" en lugar de pantalla en blanco.
 *
 * No reporta a Sentry aquí (lo hace el ErrorBoundary de Sentry si está activo).
 * Este es el último recurso para que el usuario nunca quede atrapado.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, reporting: false, reported: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Chunk de una versión anterior (tras un despliegue): recargar en vez de mostrar error.
    if (isStaleChunkError(error) && reloadForStaleChunk()) return;
    // Logueamos a consola — Sentry (si está) lo capturará por su cuenta vía window.onerror.
    console.error('[ErrorBoundary]', error, info?.componentStack);
    this.setState({ errorInfo: info?.componentStack || null });
  }

  handleGoHome = () => {
    // Reset al estado raíz: limpia path + recarga para que la app reinicie limpia.
    try {
      window.history.replaceState({}, '', '/');
    } catch (_) {}
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, reported: false });
  };

  handleReport = async () => {
    if (this.state.reporting || this.state.reported) return;
    this.setState({ reporting: true });
    try {
      await api.reportError({
        message: this.state.error?.message || String(this.state.error) || 'Error desconocido',
        stack: this.state.error?.stack || null,
        page: 'ErrorBoundary',
        url: window.location.href,
        user_agent: navigator.userAgent,
        extra: { component_stack: this.state.errorInfo },
      });
      this.setState({ reported: true });
    } catch (e) {
      console.error('reportError failed:', e);
      // Aún así marcamos reportado para que el usuario no spamee — el SA verá
      // el error en logs del servidor de todos modos.
      this.setState({ reported: true });
    } finally {
      this.setState({ reporting: false });
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg, #f8fafc)', padding: 24,
        fontFamily: "'DM Sans', -apple-system, sans-serif",
      }}>
        <div style={{
          maxWidth: 520, width: '100%', textAlign: 'center',
          background: 'var(--card, #fff)', padding: 36, borderRadius: 16,
          border: '1px solid var(--border, #e2e8f0)', boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16, margin: '0 auto 20px',
            background: 'color-mix(in srgb, #EF4444 14%, transparent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AlertTriangle size={32} color="#EF4444" />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 8px', color: 'var(--text, #111827)' }}>
            Algo se ha roto
          </h1>
          <p style={{ fontSize: 14, color: 'var(--text-muted, #6B7280)', margin: '0 0 24px', lineHeight: 1.5 }}>
            La página ha tenido un error inesperado. No te preocupes — tus datos están a salvo.
            Vuelve al inicio o reinténtalo.
          </p>
          {process.env.NODE_ENV !== 'production' && this.state.error && (
            <details style={{
              textAlign: 'left', background: 'var(--surface-alt, #f1f5f9)', padding: 12,
              borderRadius: 8, marginBottom: 20, fontSize: 11,
              fontFamily: 'var(--font-mono, monospace)', color: 'var(--text-muted)',
            }}>
              <summary style={{ cursor: 'pointer', fontWeight: 700, marginBottom: 6 }}>Detalles del error (dev only)</summary>
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {this.state.error?.message || String(this.state.error)}
              </div>
            </details>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
            <button onClick={this.handleGoHome} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 24px', background: 'var(--accent, #2563EB)', color: '#fff',
              border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer',
            }}>
              <Home size={16} /> Volver al inicio
            </button>
            <button onClick={this.handleRetry} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 24px', background: 'transparent', color: 'var(--text-muted, #6B7280)',
              border: '1px solid var(--border, #e2e8f0)', borderRadius: 10,
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}>
              <RefreshCw size={16} /> Reintentar
            </button>
          </div>

          {/* Botón "Reportar problema" — envía notificación instantánea al SA
              para que pueda corregirlo cuanto antes. */}
          <div style={{ borderTop: '1px solid var(--border, #e2e8f0)', paddingTop: 14 }}>
            <p style={{ fontSize: 12, color: 'var(--text-muted, #6B7280)', margin: '0 0 10px' }}>
              ¿Quieres que el equipo lo arregle cuanto antes?
            </p>
            <button onClick={this.handleReport} disabled={this.state.reporting || this.state.reported} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 20px',
              background: this.state.reported ? 'color-mix(in srgb, #10B981 14%, transparent)' : 'transparent',
              color: this.state.reported ? '#059669' : '#EF4444',
              border: `1px solid ${this.state.reported ? '#10B981' : '#EF4444'}`,
              borderRadius: 10, fontSize: 13, fontWeight: 700,
              cursor: this.state.reported ? 'default' : 'pointer',
              opacity: this.state.reporting ? 0.6 : 1,
            }}>
              {this.state.reported ? (
                <><CheckCircle size={14} /> Reporte enviado — gracias</>
              ) : this.state.reporting ? (
                <><RefreshCw size={14} className="spin" /> Enviando reporte…</>
              ) : (
                <><Send size={14} /> Reportar problema al equipo</>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
